import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi, type SkillSummary } from "../lib/api.ts";
import { useAsync } from "../lib/useAsync.ts";
import { formatRelative } from "../lib/format.ts";
import { basename, skillHref, unitLabel } from "../lib/skillKey.ts";
import { CopyField } from "../components/CopyField.tsx";
import {
  EmptyState,
  ErrorState,
  PageHeader,
  Spinner,
} from "../components/States.tsx";

const SCOPES = [
  ["All", "all"],
  ["Org", "org"],
  ["User", "user"],
  ["Project", "project"],
] as const;

type ScopeKey = (typeof SCOPES)[number][1];

export default function Skills() {
  const api = useApi();
  const fetcher = useCallback(
    (signal: AbortSignal) => api.listSkills({}, signal),
    [api],
  );
  const { data, error, loading, reload } = useAsync(fetcher, [api]);
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState<ScopeKey>("all");

  const counts = useMemo(() => {
    const items = data ?? [];
    const c: Record<ScopeKey, number> = {
      all: items.length,
      org: 0,
      user: 0,
      project: 0,
    };
    for (const s of items) {
      if (s.scope in c) c[s.scope as ScopeKey]++;
    }
    return c;
  }, [data]);

  const rows = useMemo(() => {
    let items = data ?? [];
    if (scope !== "all") items = items.filter((s) => s.scope === scope);
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((s) =>
      [
        s.name,
        s.agent,
        s.project,
        s.author,
        ...s.skills.map((k) => `${k.name} ${k.description}`),
      ].some((f) => f?.toLowerCase().includes(q)),
    );
  }, [data, query, scope]);

  return (
    <section>
      <PageHeader
        title="Skills"
        subtitle="Skills, agents, commands and CLAUDE.md synced to this organization — by scope."
        actions={
          data && data.length > 0 ? (
            <input
              className="input input--search"
              type="search"
              placeholder="Filter by name, agent, project…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          ) : null
        }
      />

      {loading && <Spinner />}
      {error && <ErrorState error={error} onRetry={reload} />}

      {data && data.length === 0 && <SkillsEmpty />}

      {data && data.length > 0 && (
        <>
          <div className="scope-rail" role="tablist">
            {SCOPES.map(([label, key]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={scope === key}
                className={
                  scope === key ? "scope-link scope-link--active" : "scope-link"
                }
                onClick={() => setScope(key)}
              >
                {label}
                <span className="scope-count">{counts[key]}</span>
              </button>
            ))}
          </div>

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Skill</th>
                  <th>Scope</th>
                  <th>Agent</th>
                  <th>Project</th>
                  <th className="num">Version</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <SkillRow key={rowKey(s)} skill={s} />
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="table-empty">
                      No skills match
                      {query ? ` “${query}”` : ` the ${scope} scope`}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="dim" style={{ margin: "1.25rem 0 0", fontSize: "0.8rem" }}>
            Org skills are pushed by admins and pulled by every member with{" "}
            <code className="inline-code">stift pull --skills</code>.
          </p>
        </>
      )}
    </section>
  );
}

function rowKey(s: SkillSummary): string {
  return `${s.scope}|${s.agent}|${s.project}|${s.name}`;
}

function SkillRow({ skill: s }: { skill: SkillSummary }) {
  const desc = s.skills[0]?.description ?? (s.name === "CLAUDE.md" ? "Global instructions" : "");
  return (
    <tr>
      <td>
        <Link to={skillHref(s)} className="table-name">
          {unitLabel(s.name)}
        </Link>
        {desc && (
          <div className="dim" style={{ fontSize: "0.78rem" }}>
            {desc}
          </div>
        )}
      </td>
      <td>
        <span className={s.scope === "org" ? "badge badge--admin" : "badge"}>
          {s.scope}
        </span>
      </td>
      <td>
        <span className="badge badge--agent">{s.agent}</span>
      </td>
      <td className="mono dim">
        {s.project ? <span title={s.project}>{basename(s.project)}</span> : "—"}
      </td>
      <td className="num mono">v{s.version}</td>
      <td className="dim" title={s.updated_at ?? undefined}>
        {s.author || "?"} · {formatRelative(s.updated_at)}
      </td>
    </tr>
  );
}

function SkillsEmpty() {
  return (
    <EmptyState title="No skills yet">
      <p>
        Push your Claude Code skills, agents, commands and CLAUDE.md from any
        machine:
      </p>
      <CopyField value="stift push --skills" prompt="$" multiline />
      <p className="dim">
        Admins can share a set with the whole org using{" "}
        <code className="inline-code">--scope org</code>. Settings and MCP
        config are never synced.
      </p>
    </EmptyState>
  );
}
